import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { platformApi } from '@/services/platform.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Platform-wide storage administration.
 *
 * Not a tenant page — every request here is gated on `platform.settings.*`,
 * which only `PLATFORM_SUPER_ADMIN` ever holds. A saved row overrides the
 * STORAGE_DRIVER/S3_* env vars the service would otherwise fall back to.
 */
export function StorageSettingsPage() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['platform', 'storage-settings'],
    queryFn: () => platformApi.getStorageSettings(),
  });

  // Each field is `null` until the caller edits it — the visible value falls
  // back to whatever the query loaded, the same pattern smtp-settings-page
  // already uses, so there is no effect needed to "seed" the form once data
  // arrives.
  const [driver, setDriver] = useState<'local' | 's3' | null>(null);
  const [s3Bucket, setS3Bucket] = useState<string | null>(null);
  const [s3Region, setS3Region] = useState<string | null>(null);
  const [s3Endpoint, setS3Endpoint] = useState<string | null>(null);
  const [s3AccessKeyId, setS3AccessKeyId] = useState<string | null>(null);
  const [s3SecretAccessKey, setS3SecretAccessKey] = useState('');
  const [isChangingSecret, setIsChangingSecret] = useState(false);
  const [s3ForcePathStyle, setS3ForcePathStyle] = useState<boolean | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const effectiveDriver = driver ?? settings.data?.driver ?? 'local';
  const showSecretInput = isChangingSecret || settings.data?.hasS3SecretAccessKey === false;

  const save = useMutation({
    mutationFn: () =>
      platformApi.updateStorageSettings({
        driver: effectiveDriver,
        s3Bucket: (s3Bucket ?? settings.data?.s3Bucket) || null,
        s3Region: (s3Region ?? settings.data?.s3Region) || null,
        s3Endpoint: (s3Endpoint ?? settings.data?.s3Endpoint) || null,
        s3AccessKeyId: (s3AccessKeyId ?? settings.data?.s3AccessKeyId) || null,
        s3SecretAccessKey: showSecretInput && s3SecretAccessKey ? s3SecretAccessKey : undefined,
        s3ForcePathStyle: s3ForcePathStyle ?? settings.data?.s3ForcePathStyle ?? true,
      }),
    onSuccess: async () => {
      setSaveError(null);
      setIsChangingSecret(false);
      setS3SecretAccessKey('');
      await queryClient.invalidateQueries({ queryKey: ['platform', 'storage-settings'] });
    },
    onError: (error: unknown) => {
      setSaveError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.storage.title')}</h1>
          <p className="page-count">{t('platform.storage.subtitle')}</p>
        </div>
      </header>

      {settings.isError && (
        <Alert tone="danger">
          {settings.error instanceof ApiError
            ? t(`errors.${settings.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {settings.data && (
        <div className="settings-status">
          <div>
            <p className="settings-status-title">{t('platform.storage.currentStatus')}</p>
            <p className="settings-status-hint">
              {t(`platform.storage.source.${settings.data.source}`)}
            </p>
            {settings.data.updatedAt && (
              <p className="settings-status-hint">
                {t('platform.storage.lastUpdate', {
                  values: { date: formatDateTime(settings.data.updatedAt, locale) },
                })}
              </p>
            )}
          </div>
          <span
            className="badge"
            data-status={settings.data.source === 'database' ? 'ACTIVE' : undefined}
          >
            {t(`platform.storage.sourceBadge.${settings.data.source}`)}
          </span>
        </div>
      )}

      <form
        className="settings-card"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <h2 className="settings-card-title">{t('platform.storage.driver')}</h2>

        <div className="field">
          <label className="field-label" htmlFor="storage-driver">
            {t('platform.storage.driverLabel')}
          </label>
          <select
            id="storage-driver"
            className="field-input"
            value={effectiveDriver}
            onChange={(event) => setDriver(event.target.value as 'local' | 's3')}
          >
            <option value="local">{t('platform.storage.driverLocal')}</option>
            <option value="s3">{t('platform.storage.driverS3')}</option>
          </select>
        </div>

        {effectiveDriver === 's3' && (
          <>
            <div className="form-grid">
              <Field
                label={t('platform.storage.bucket')}
                name="s3Bucket"
                value={s3Bucket ?? settings.data?.s3Bucket ?? ''}
                onChange={(event) => setS3Bucket(event.target.value)}
                required
              />
              <Field
                label={t('platform.storage.region')}
                name="s3Region"
                value={s3Region ?? settings.data?.s3Region ?? 'us-east-1'}
                onChange={(event) => setS3Region(event.target.value)}
              />
            </div>

            <Field
              label={t('platform.storage.endpoint')}
              name="s3Endpoint"
              value={s3Endpoint ?? settings.data?.s3Endpoint ?? ''}
              onChange={(event) => setS3Endpoint(event.target.value)}
              hint={t('platform.storage.endpointHint')}
              placeholder="http://localhost:9000"
              dir="ltr"
            />

            <div className="form-grid">
              <Field
                label={t('platform.storage.accessKeyId')}
                name="s3AccessKeyId"
                value={s3AccessKeyId ?? settings.data?.s3AccessKeyId ?? ''}
                onChange={(event) => setS3AccessKeyId(event.target.value)}
                required
                dir="ltr"
              />
              <div className="field">
                <div className="settings-password-label">
                  <label className="field-label" htmlFor="s3-secret-key">
                    {t('platform.storage.secretAccessKey')}
                  </label>
                  {!showSecretInput && (
                    <button
                      type="button"
                      className="settings-inline-link"
                      onClick={() => setIsChangingSecret(true)}
                    >
                      {t('platform.storage.changeSecret')}
                    </button>
                  )}
                </div>
                {showSecretInput ? (
                  <input
                    id="s3-secret-key"
                    type="password"
                    className="field-input"
                    value={s3SecretAccessKey}
                    onChange={(event) => setS3SecretAccessKey(event.target.value)}
                    autoComplete="new-password"
                    dir="ltr"
                    required={settings.data?.hasS3SecretAccessKey === false}
                  />
                ) : (
                  <p className="settings-password-stored">{t('platform.storage.secretStored')}</p>
                )}
              </div>
            </div>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={s3ForcePathStyle ?? settings.data?.s3ForcePathStyle ?? true}
                onChange={(event) => setS3ForcePathStyle(event.target.checked)}
              />
              <span>{t('platform.storage.forcePathStyle')}</span>
            </label>
          </>
        )}

        {saveError && <Alert tone="danger">{saveError}</Alert>}

        <Button type="submit" variant="primary" loading={save.isPending}>
          {t('common.actions.save')}
        </Button>
      </form>
    </div>
  );
}
